/**
 * hooks/useGameSession.js
 *
 * Shared hook for all game pages (spells, locations, npcs, lore).
 * Handles: state management, fetch, guess, surrender, achievements, guest ID.
 *
 * Usage:
 *   const session = useGameSession({ gameName: "spells", itemIdKey: "spell_id" })
 */

import { useState, useEffect, useCallback, useRef, useReducer } from "react"
import { useUser } from "./useUser"
import { supabase } from "../lib/supabaseClient"

const MAX_ATTEMPTS = 5
const FADE_OUT_MS  = 300

// ─── Guest UUID persistence ──────────────────────────────
const GUEST_UUID_KEY = "azerodle_guest_id"

function getGuestId() {
  try { return localStorage.getItem(GUEST_UUID_KEY) || null }
  catch { return null }
}
function saveGuestId(id) {
  try { localStorage.setItem(GUEST_UUID_KEY, id) } catch {}
}

// ─── Game reducer ─────────────────────────────────────────
const initialGameState = {
  answers: [],
  hints: {},
  finished: false,
  correctAnswerName: null,
  animateAttempts: false,
  shouldAnimateHints: false,
  shouldAnimateProgress: false,
  progressWidth: 0,
}

function gameReducer(state, action) {
  switch (action.type) {
    case "LOAD_STATE": {
      const answers = action.answers || []
      return {
        ...state,
        answers,
        hints: action.hints || {},
        finished: action.finished || false,
        correctAnswerName: action.correctAnswerName || null,
        animateAttempts: false,
        shouldAnimateHints: false,
        shouldAnimateProgress: false,
        progressWidth: (answers.length / MAX_ATTEMPTS) * 100,
      }
    }
    case "RESET":
      return { ...initialGameState }
    case "NEW_GUESS": {
      const exists = state.answers.some(a => a.attempt_number === action.answer.attempt_number)
      if (exists) return state
      const answers = [...state.answers, action.answer]
      return {
        ...state,
        answers,
        hints: action.hints || state.hints,
        finished: action.finished,
        correctAnswerName: action.correctAnswerName || state.correctAnswerName,
        animateAttempts: true,
        shouldAnimateHints: !!action.hints,
        shouldAnimateProgress: true,
        progressWidth: (answers.length / MAX_ATTEMPTS) * 100,
      }
    }
    case "CLEAR_ANIMATE_ATTEMPTS":
      return { ...state, animateAttempts: false }
    default:
      return state
  }
}

// ─── Midnight countdown (UTC — matches server daily reset) ──
export function useMidnightCountdown() {
  const [time, setTime] = useState("")
  useEffect(() => {
    const tick = () => {
      const now = new Date()
      const midnight = new Date(Date.UTC(
        now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1, 0, 0, 0
      ))
      const diff = midnight - now
      if (diff <= 0) { setTime("00:00:00"); return }
      const h = String(Math.floor(diff / 3600000)).padStart(2, "0")
      const m = String(Math.floor((diff % 3600000) / 60000)).padStart(2, "0")
      const s = String(Math.floor((diff % 60000) / 1000)).padStart(2, "0")
      setTime(`${h}:${m}:${s}`)
    }
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [])
  return time
}

// ─── Main hook ───────────────────────────────────────────
/**
 * @param {Object} config
 * @param {string} config.gameName       — "spells", "locations", etc.
 * @param {string} config.itemIdKey      — key in suggestion for item ID ("spell_id", "location_id")
 * @param {string} config.correctNameKey — backend key for correct answer name
 * @param {string} config.dailyKey       — backend key for daily item data
 * @param {function} [config.preloadDaily] — optional async fn to preload assets
 */
export function useGameSession({
  gameName,
  itemIdKey = "spell_id",
  correctNameKey = "correct_spell_name",
  dailyKey = "daily_spell",
  preloadDaily,
}) {
  const { user, loading: userLoading } = useUser()
  const isGuest = !user

  const [game, dispatch] = useReducer(gameReducer, initialGameState)

  const [gameMode, setGameMode]             = useState(() => {
    // Check URL for ?mode= param (used by Next Challenge navigation)
    const urlParams = new URLSearchParams(window.location.search)
    const urlMode = urlParams.get("mode")
    if (urlMode === "classic" || urlMode === "retail") {
      localStorage.setItem("azerodle_game_mode", urlMode)
      // Clean up the URL param so it doesn't stick around
      const url = new URL(window.location)
      url.searchParams.delete("mode")
      window.history.replaceState({}, "", url.pathname + url.search)
      return urlMode
    }
    return localStorage.getItem("azerodle_game_mode") || "classic"
  })
  const [dailyItem, setDailyItem]           = useState(null)
  const [initialLoading, setInitialLoading] = useState(true)
  const spinnerTimerRef = useRef(null)
  const [fading, setFading]                 = useState(false)
  const [iconKey, setIconKey]               = useState(0)
  const [submitting, setSubmitting]         = useState(false)
  const [selectedItem, setSelectedItem]     = useState(null)
  const [userAnswer, setUserAnswer]         = useState("")
  const [suggestions, setSuggestions]        = useState([])
  const [error, setError]                   = useState(null)
  const [showModal, setShowModal]           = useState(false)
  const [lastResult, setLastResult]         = useState(null)
  const [contentReady, setContentReady]     = useState(false)
  const [stats, setStats]                   = useState(null)
  const [xpResult, setXpResult]             = useState(null)
  const [confirmAction, setConfirmAction]   = useState(null)
  const [achievementQueue, setAchievementQueue] = useState([])
  const [showAchievement, setShowAchievement]   = useState(false)

  const [guestId, setGuestIdState] = useState(() => getGuestId())
  const setGuestId = useCallback((id) => { setGuestIdState(id); saveGuestId(id) }, [])

  useEffect(() => {
    if (game.animateAttempts) {
      const t = setTimeout(() => dispatch({ type: "CLEAR_ANIMATE_ATTEMPTS" }), 600)
      return () => clearTimeout(t)
    }
  }, [game.animateAttempts])

  const isFirstMount  = useRef(true)
  const isFirstMountFetch = useRef(false)
  const abortRef      = useRef(null)
  const debounceRef   = useRef(null)
  const switchingRef  = useRef(false)

  // Cleanup spinner delay timer on unmount
  useEffect(() => () => clearTimeout(spinnerTimerRef.current), [])

  const effectiveUserId = user?.id || guestId

  const userPayload = useCallback(() => {
    if (isGuest) return { guest: true, ...(guestId ? { user_id: guestId } : {}) }
    return { user_id: user.id }
  }, [isGuest, guestId, user?.id])

  // ─── Fetch game state ──────────────────────────────────
  const fetchDaily = useCallback(async (silent = false) => {
    if (abortRef.current) abortRef.current.abort()
    abortRef.current = new AbortController()
    const signal = abortRef.current.signal

    if (!silent) dispatch({ type: "RESET" })

    try {
      const params = new URLSearchParams({
        game_name: gameName, game_mode: gameMode,
        ...(effectiveUserId ? { user_id: effectiveUserId } : {}),
        ...(isGuest ? { guest: "true" } : {}),
      })
      const res  = await fetch(`/game_state?${params}`, { signal })
      const data = await res.json()
      if (!res.ok) return

      const daily = data[dailyKey]
      if (preloadDaily && daily) await preloadDaily(daily)

      setDailyItem(daily)
      const userAnswers = data.user_answers || []
      dispatch({
        type: "LOAD_STATE",
        answers: userAnswers,
        hints: data.hints || {},
        finished: data.already_guessed_correctly || data.surrendered || userAnswers.length >= MAX_ATTEMPTS,
        correctAnswerName: data[correctNameKey] || data.correct_answer_name || data.name || null
      })
      if (data.stats) setStats(data.stats)
      // On mode-switch fetches, mark content ready immediately.
      // On first mount this is deferred to the Promise.all callback
      // so that initialLoading and contentReady flip in the same batch.
      if (!isFirstMountFetch.current) {
        setContentReady(true)
      }
      setFading(false); switchingRef.current = false
    } catch (e) {
      if (e.name === "AbortError") return
      setError("Network error: " + e.message)
      if (!isFirstMountFetch.current) {
        setContentReady(true)
      }
      setFading(false); switchingRef.current = false
    }
  }, [effectiveUserId, isGuest, gameMode, gameName, dailyKey, correctNameKey, preloadDaily])

  const lastFetchKey = useRef(null)
  useEffect(() => {
    if (userLoading) return
    const fetchKey = `${gameMode}__${effectiveUserId ?? "anon"}`
    if (fetchKey === lastFetchKey.current) return
    lastFetchKey.current = fetchKey
    if (isFirstMount.current) {
      isFirstMount.current = false
      isFirstMountFetch.current = true

      fetchDaily(false).then(() => {
        isFirstMountFetch.current = false
        // Two nested rAFs guarantee PageTransition has painted its
        // visible class before we swap spinner → content.
        // This prevents the mode-buttons-only flash.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setContentReady(true)
            setInitialLoading(false)
          })
        })
      })
    } else if (!initialLoading) {
      fetchDaily(true)
    }
  }, [gameMode, effectiveUserId, userLoading]) // eslint-disable-line

  // ─── Mode switching ────────────────────────────────────
  const switchMode = useCallback((mode) => {
    if (mode === gameMode || switchingRef.current) return
    switchingRef.current = true; setFading(true)
    setTimeout(() => {
      // Don't setContentReady(false) — old content stays in DOM
      // (hidden by opacity:0 from fading) to preserve panel height.
      // Data resets below will be overwritten by the new fetch.
      setDailyItem(null); setIconKey(k => k + 1)
      setGameMode(mode); setShowModal(false); setXpResult(null)
      setConfirmAction(null); setAchievementQueue([]); setShowAchievement(false)
      localStorage.setItem("azerodle_game_mode", mode)
    }, FADE_OUT_MS)
  }, [gameMode])

  const refreshDailyItem = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        game_name: gameName, game_mode: gameMode,
        ...(effectiveUserId ? { user_id: effectiveUserId } : {}),
        ...(isGuest ? { guest: "true" } : {}),
      })
      const res  = await fetch(`/game_state?${params}`)
      const data = await res.json()
      if (res.ok && data[dailyKey]) setDailyItem(data[dailyKey])
    } catch (e) {
      console.error("[useGameSession] refreshDailyItem failed:", e)
    }
  }, [effectiveUserId, isGuest, gameMode, gameName, dailyKey])

  // ─── Shared guess submission ───────────────────────────
  const sendGuess = async (payload) => {
    setError(null); setSubmitting(true)
    try {
      const res = await fetch("/make_guess", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      })
      const data = await res.json()
      if (!res.ok) { setError(data.error); setSubmitting(false); return }
      if (isGuest && data.guest_id) setGuestId(data.guest_id)

      const isFinished = data.already_guessed_correctly || data.attempts_today >= MAX_ATTEMPTS || data.surrendered
      if (data.new_answer) {
        dispatch({ type: "NEW_GUESS", answer: data.new_answer, hints: data.hints || null, finished: isFinished, correctAnswerName: data[correctNameKey] || null })
      } else if (isFinished) {
        dispatch({ type: "LOAD_STATE", answers: game.answers, hints: data.hints || game.hints, finished: true, correctAnswerName: data[correctNameKey] || game.correctAnswerName })
      }

      const daily = data[dailyKey]
      if (daily) { setDailyItem(daily); setIconKey(k => k + 1) } else { refreshDailyItem() }
      if (isFinished) {
        if (data.stats) setStats(data.stats)
        if (data.xp_result) setXpResult(data.xp_result)
        setLastResult({ correct: data.already_guessed_correctly, attempt_number: data.attempts_today, correctAnswerName: data[correctNameKey] || null })
        setShowModal(true)
      }
    } catch (e) { setError("Network error: " + e.message) }
    setSelectedItem(null); setUserAnswer(""); setSuggestions([]); setSubmitting(false)
  }

  const makeGuess = async () => {
    if (!selectedItem || submitting) return
    await sendGuess({ game_name: gameName, game_mode: gameMode, user_answer: selectedItem[itemIdKey], ...userPayload() })
  }

  const doSkipAttempt = async () => {
    if (submitting) return; setConfirmAction(null)
    await sendGuess({ game_name: gameName, game_mode: gameMode, skip: true, ...userPayload() })
  }

  const doSurrender = async () => {
    if (submitting) return; setConfirmAction(null); setError(null); setSubmitting(true)
    try {
      const res = await fetch("/surrender", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ game_name: gameName, game_mode: gameMode, ...userPayload() }),
      })
      const data = await res.json()
      if (!res.ok) { setError(data.error); setSubmitting(false); return }
      if (isGuest && data.guest_id) setGuestId(data.guest_id)
      dispatch({ type: "LOAD_STATE", answers: game.answers, hints: data.hints || game.hints, finished: true, correctAnswerName: data[correctNameKey] || game.correctAnswerName })
      const daily = data[dailyKey]
      if (daily) { setDailyItem(daily); setIconKey(k => k + 1) } else { refreshDailyItem() }
      if (data.stats) setStats(data.stats)
      if (data.xp_result) setXpResult(data.xp_result)
      setLastResult({ correct: false, attempt_number: data.attempts_today, correctAnswerName: data[correctNameKey] || null }); setShowModal(true)
    } catch (e) { setError("Network error: " + e.message) }
    setSelectedItem(null); setUserAnswer(""); setSuggestions([]); setSubmitting(false)
  }

  const handleConfirm = useCallback(() => {
    if (confirmAction === "skip") doSkipAttempt()
    if (confirmAction === "surrender") doSurrender()
  }, [confirmAction]) // eslint-disable-line
  const handleCancelConfirm = useCallback(() => setConfirmAction(null), [])

  // ─── Achievements ──────────────────────────────────────
  const fetchAchievements = useCallback(async () => {
    if (isGuest || !user?.id) return
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) return
      const res = await fetch("/api/achievements/check", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ user_id: user.id, game_mode: gameMode }),
      })
      const data = await res.json()
      if (data.achievements?.length > 0) { setAchievementQueue(data.achievements); setShowAchievement(true) }
    } catch (err) { console.error("Achievement check failed:", err) }
  }, [isGuest, user?.id, gameMode])

  const handleResultClose = useCallback(async () => {
    setShowModal(false); if (game.finished) await fetchAchievements()
  }, [game.finished, fetchAchievements])

  const achCheckedForMode = useRef(null)
  useEffect(() => {
    if (userLoading || !contentReady || isGuest || !user?.id) return
    if (achCheckedForMode.current === gameMode) return
    achCheckedForMode.current = gameMode; fetchAchievements()
  }, [userLoading, contentReady, isGuest, user?.id, gameMode, fetchAchievements])

  const handleAchievementClose = useCallback(() => {
    setAchievementQueue((prev) => { const next = prev.slice(1); if (next.length === 0) setShowAchievement(false); return next })
  }, [])

  // ─── Search ────────────────────────────────────────────
    const fetchSuggestions = useCallback(async (query) => {
      if (!query) {
        setSuggestions([])
        setSelectedItem(null)
        return
      }

      try {
        const res = await fetch(
          `/search_?${new URLSearchParams({
            game_name: gameName,
            game_mode: gameMode,
            query
          })}`
        )

        const data = await res.json()

        setSuggestions(res.ok ? data.results : [])
        setSelectedItem(null)
      } catch {
        setSuggestions([])
        setSelectedItem(null)
      }
    }, [gameName, gameMode])

  const handleChange = useCallback((e) => {
    const val = e.target.value; setUserAnswer(val)
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 250)
  }, [fetchSuggestions])

  const handleSelect = useCallback((item) => { setSelectedItem(item); setUserAnswer(item.name); setSuggestions([]) }, [])

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && selectedItem && !submitting) makeGuess()
    if (e.key === "Escape") { setSuggestions([]); if (confirmAction) setConfirmAction(null) }
  }

  const correctAnswer = game.answers.find(a => a.correct)
  const didWin        = !!correctAnswer
  const attemptsUsed  = game.answers.length
  const showBody      = !initialLoading && contentReady

  return {
    user, userLoading, isGuest,
    game, dispatch, gameMode, switchMode,
    dailyItem, iconKey, initialLoading, contentReady, showBody, fading,
    userAnswer, setUserAnswer, suggestions, setSuggestions,
    selectedItem, setSelectedItem, handleChange, handleSelect, handleKeyDown,
    submitting, error,
    makeGuess, doSkipAttempt, doSurrender,
    confirmAction, setConfirmAction, handleConfirm, handleCancelConfirm,
    showModal, lastResult, stats, xpResult, handleResultClose,
    achievementQueue, showAchievement, handleAchievementClose,
    correctAnswer, didWin, attemptsUsed, MAX_ATTEMPTS,
  }
}

export { MAX_ATTEMPTS }